import { Autocomplete, Box, Divider, Select, Stack, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { Controller } from "react-hook-form";

import { FormControl } from "@/Components/v2/atoms/FormControl/FormControl";
import { MenuItem } from "@/Components/v2/atoms/MenuItem/MenuItem";
import { TextField } from "@/Components/v2/atoms/TextField";
import { BaseTypography } from "@/Components/v2/atoms/Typography";
import { Typography } from "@/Components/v2/atoms/Typography/Typography";
import { DatePicker } from "@/Components/v2/molecule/DatePicker";
import { FormCard } from "@/Components/v2/molecule/FormContainer";
import { brandColors } from "@/Components/v2/utils/brand-colors";
import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";

import { useGeoEnabled } from "../../hooks/useGeoEnabled";
import { useScenarioForms } from "./ScenarioFormProvider";
import { ScenarioTypeEnum, ScenarioType } from "./types";
import {
  ALL_GEOGRAPHIES_LABEL,
  SHORT_SCENARIO_WARNING_MESSAGE,
  SHORT_SCENARIO_WARNING_DURATION_IN_MONTHS,
  MIN_SCENARIO_DURATION_IN_DAYS,
} from "./utils/constants";
import { useFeatureGate } from "@statsig/react-bindings";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { ToggleButtonGroup } from "@/Components/v2/molecule/ButtonGroup";
import { ToggleButton } from "@/Components/v2/atoms/Button";
import { TypeLabel } from "@/Components/v2/atoms/TypeLabel";

type GeographyOption = {
  isAllGeos: boolean;
  value: string;
};

export const checkIfShortDateRangeSelected = (
  startDate: dayjs.Dayjs | undefined,
  endDate: dayjs.Dayjs | undefined
) => {
  if (!startDate || !endDate) return false;
  const differenceInMonths = endDate.diff(startDate, "month");
  return differenceInMonths < SHORT_SCENARIO_WARNING_DURATION_IN_MONTHS;
};

export const ScenarioInformationForm: React.FC = () => {
  const {
    scenario,
    scenarioInformationForm,
    handleScenarioInformationChange,
    brandGeographies,
    allowedDateRange,
  } = useScenarioForms();
  const geoEnabled = useGeoEnabled();
  const isGeoEnabled = !geoEnabled.isLoading && geoEnabled.isEnabled;
  const theme = useTheme();
  const { getBCPHierarchyLabel } = useDefaultNodeNameInfo();
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);

  const { value: scenarioBuilderEnableNewConstraints } = useFeatureGate(
    featureFlag.scenarioBuilderConstraintsFromApi
  );

  const handleNameChange = (value: string) => {
    handleScenarioInformationChange("name", value);
  };

  const handleDescriptionChange = (value: string) => {
    handleScenarioInformationChange("description", value);
  };

  const handleBusinessUnitChange = (value: string) => {
    handleScenarioInformationChange("scenarioTarget", value);
  };

  const handleGeographyChange = (value: string | null) => {
    handleScenarioInformationChange("geography", value);
  };

  const handleScenarioTypeChange = (value: ScenarioTypeEnum) => {
    handleScenarioInformationChange("type", value);
  };

  const handleStartDateChange = useCallback(
    (value: dayjs.Dayjs | null) => {
      handleScenarioInformationChange("startDate", value ?? null);
    },
    [handleScenarioInformationChange]
  );

  const handleEndDateChange = useCallback(
    (value: dayjs.Dayjs | null) => {
      handleScenarioInformationChange("endDate", value ?? null);
    },
    [handleScenarioInformationChange]
  );

  const selectedBusinessUnit = scenarioInformationForm.watch("scenarioTarget");

  const customerGeographiesWithAllGeos = useMemo<GeographyOption[]>(() => {
    const geographies = brandGeographies?.find(
      (brand) => brand.brand === selectedBusinessUnit
    )?.geographies;

    if (!geographies) {
      return [];
    }

    const geographyOptions = geographies?.map((geography) => ({
      isAllGeos: false,
      value: geography,
    }));

    // Combine all options with "All Geographies" first if available
    return [{ isAllGeos: true, value: ALL_GEOGRAPHIES_LABEL }, ...geographyOptions];
  }, [brandGeographies, selectedBusinessUnit]);

  const formStartDate = scenarioInformationForm.watch("startDate");
  const formEndDate = scenarioInformationForm.watch("endDate");

  const minScenarioStartDate = dayjs.utc(allowedDateRange?.startDate);
  const maxScenarioEndDate = dayjs.utc(allowedDateRange?.endDate);

  const minScenarioEndDate = (formStartDate || minScenarioStartDate).add(
    MIN_SCENARIO_DURATION_IN_DAYS,
    "day"
  );

  const isShortDateRangeSelected = checkIfShortDateRangeSelected(formStartDate, formEndDate);

  const businessUnitOptions = useMemo(() => {
    return brandGeographies?.map((brandGeography) => ({
      value: brandGeography.brand,
      label: getBCPHierarchyLabel({
        brand: brandGeography.brand,
        category: "default",
        product: "default",
      }),
    }));
  }, [brandGeographies, getBCPHierarchyLabel]);

  return (
    <FormCard title="Information" subtitle="Set a name, scope and time period for your scenario.">
      <Stack spacing={2.5}>
        <FormControl fullWidth>
          <Typography fontWeight={600} mb={1} variant="body1">
            Scenario Name
          </Typography>
          <Controller
            name="name"
            control={scenarioInformationForm.control}
            render={({ field, fieldState }) => (
              <>
                <TextField
                  {...field}
                  onChange={(e) => handleNameChange(e.target.value)}
                  id="name"
                  size="small"
                  error={fieldState.error ? true : false}
                  sx={{ maxWidth: "400px" }}
                />
                {fieldState.error && (
                  <BaseTypography variant="body2" color={brandColors.delete} mt={1}>
                    {fieldState.error.message}
                  </BaseTypography>
                )}
              </>
            )}
          />
        </FormControl>

        <FormControl fullWidth>
          <Typography fontWeight={600} mb={1} variant="body1">
            Scenario Description (optional)
          </Typography>
          <Controller
            name="description"
            control={scenarioInformationForm.control}
            render={({ field }) => (
              <TextField
                {...field}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                id="description"
                multiline
                rows={2}
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", sm: "600px", md: "800px" },
                }}
              />
            )}
          />
        </FormControl>

        <Box>
          <Typography fontWeight={600} mb={1} variant="body1">
            Scenario Type
          </Typography>
          {scenarioBuilderEnableNewConstraints ? (
            <FormControl fullWidth>
              <Controller
                name="type"
                control={scenarioInformationForm.control}
                render={({ field }) => (
                  <ToggleButtonGroup
                    {...field}
                    exclusive
                    onChange={(_event, value) => {
                      if (value !== null) {
                        handleScenarioTypeChange(value);
                      }
                    }}
                    aria-label="scenario type"
                    sx={{ maxWidth: "200px" }}
                  >
                    <ToggleButton value={ScenarioTypeEnum.Budget} aria-label="forecast">
                      Forecast
                    </ToggleButton>
                    <ToggleButton value={ScenarioTypeEnum.Optimizer} aria-label="optimizer">
                      Optimize
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </FormControl>
          ) : (
            <Box display="flex" justifyContent="flex-start">
              <TypeLabel
                label={ScenarioType.toString(scenario?.scenarioType as ScenarioTypeEnum)}
                labelColor={
                  scenario?.scenarioType === "optimizer"
                    ? theme.palette.scenarioType.optimizer
                    : theme.palette.scenarioType.forecast
                }
                size="medium"
              />
            </Box>
          )}
        </Box>

        <FormControl fullWidth>
          <Typography fontWeight={600} mb={1} variant="body1">
            Business Unit
          </Typography>
          <Controller
            name="scenarioTarget"
            control={scenarioInformationForm.control}
            render={({ field, fieldState }) => (
              <>
                <Select
                  {...field}
                  value={field.value ?? null}
                  labelId="scenarioTarget-label"
                  id="scenarioTarget"
                  size="small"
                  onChange={(event) => {
                    handleBusinessUnitChange(event.target.value!);
                  }}
                  sx={{ maxWidth: "400px" }}
                  disabled={!brandGeographies}
                >
                  {businessUnitOptions?.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {fieldState.error && (
                  <BaseTypography variant="body2" color={brandColors.delete} mt={1}>
                    {fieldState.error.message || "Business Unit is required"}
                  </BaseTypography>
                )}
              </>
            )}
          />
        </FormControl>

        {isGeoEnabled && (
          <FormControl fullWidth sx={{ maxWidth: "25rem" }}>
            <Typography fontWeight={600} mb={1} variant="body1">
              Geographic Scope
            </Typography>
            <Controller
              name="geography"
              control={scenarioInformationForm.control}
              render={({ field, fieldState }) => {
                const currentGeography = field.value
                  ? { isAllGeos: false, value: field.value }
                  : { isAllGeos: true, value: ALL_GEOGRAPHIES_LABEL };

                return (
                  <>
                    <Autocomplete<GeographyOption | null, false, true>
                      value={currentGeography}
                      getOptionLabel={(option) => option?.value ?? ""}
                      options={customerGeographiesWithAllGeos}
                      onChange={(_event, value) => {
                        const newValue = value?.isAllGeos ? null : value?.value;
                        handleGeographyChange(newValue);
                      }}
                      disableClearable
                      disabled={!selectedBusinessUnit}
                      id="geography"
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Scenario Geography"
                          error={fieldState.error ? true : false}
                        />
                      )}
                      renderOption={(props, option) => {
                        if (!option) {
                          return null;
                        }

                        if (option.isAllGeos) {
                          return (
                            <>
                              <Typography {...props}>{option.value}</Typography>
                              <Divider sx={{ marginY: "0.5rem" }} />
                            </>
                          );
                        }
                        return <Typography {...props}>{option.value}</Typography>;
                      }}
                    />
                    {fieldState.error && (
                      <BaseTypography variant="body2" color={brandColors.delete} mt={1}>
                        {fieldState.error.message}
                      </BaseTypography>
                    )}
                  </>
                );
              }}
            />
          </FormControl>
        )}

        <Stack direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
          <Stack>
            <Typography variant="body1" fontWeight={600} mb={1}>
              Start Date
            </Typography>
            <Box width={240}>
              <Controller
                name="startDate"
                control={scenarioInformationForm.control}
                render={({ field, fieldState }) => (
                  <>
                    <DatePicker
                      name="startDate"
                      views={["year", "month", "day"]}
                      format="D MMMM YYYY"
                      openTo="day"
                      disabled={!allowedDateRange}
                      value={field.value}
                      onAccept={handleStartDateChange}
                      open={startDateOpen}
                      onOpen={() => setStartDateOpen(true)}
                      onClose={() => {
                        setStartDateOpen(false);
                        scenarioInformationForm.trigger("startDate");
                      }}
                      minDate={minScenarioStartDate}
                      referenceDate={minScenarioStartDate}
                      timezone="UTC"
                      slotProps={{
                        field: {
                          shouldRespectLeadingZeros: true,
                        },
                        textField: {
                          error: !!fieldState.error,
                          onBlur: () => scenarioInformationForm.trigger("startDate"),
                          size: "small",
                          onClick: () => allowedDateRange && setStartDateOpen(true),
                          onMouseDown: (e: React.MouseEvent) => e.preventDefault(),
                          sx: {
                            "& input": {
                              cursor: "pointer",
                            },
                          },
                          inputProps: {
                            readOnly: true,
                          },
                        },
                      }}
                    />
                    {fieldState.error && (
                      <BaseTypography variant="body2" color={brandColors.delete} mt={1}>
                        {fieldState.error.message}
                      </BaseTypography>
                    )}
                  </>
                )}
              />
            </Box>
          </Stack>

          <Stack>
            <Typography variant="body1" fontWeight={600} mb={1}>
              End Date
            </Typography>
            <Box width={240}>
              <Controller
                name="endDate"
                control={scenarioInformationForm.control}
                render={({ field, fieldState }) => (
                  <>
                    <DatePicker
                      name="endDate"
                      views={["year", "month", "day"]}
                      format="D MMMM YYYY"
                      openTo="day"
                      disabled={!allowedDateRange}
                      minDate={minScenarioEndDate}
                      maxDate={maxScenarioEndDate}
                      referenceDate={minScenarioEndDate}
                      timezone="UTC"
                      value={field.value}
                      onAccept={handleEndDateChange}
                      open={endDateOpen}
                      onOpen={() => setEndDateOpen(true)}
                      onClose={() => {
                        setEndDateOpen(false);
                        scenarioInformationForm.trigger("endDate");
                      }}
                      slotProps={{
                        field: {
                          shouldRespectLeadingZeros: true,
                        },
                        textField: {
                          error: !!fieldState.error,
                          onBlur: () => scenarioInformationForm.trigger("endDate"),
                          size: "small",
                          onClick: () => allowedDateRange && setEndDateOpen(true),
                          onMouseDown: (e: React.MouseEvent) => e.preventDefault(),
                          sx: {
                            "& input": {
                              cursor: "pointer",
                            },
                          },
                          inputProps: {
                            readOnly: true,
                          },
                        },
                      }}
                    />
                    {fieldState.error && (
                      <BaseTypography
                        variant="body2"
                        color={brandColors.delete}
                        mt={1}
                        maxWidth={600}
                      >
                        {fieldState.error.message}
                      </BaseTypography>
                    )}
                  </>
                )}
              />
            </Box>
          </Stack>
        </Stack>
        {/* Only show the warning message if no errors are present */}
        {!scenarioInformationForm.formState.errors?.startDate &&
          !scenarioInformationForm.formState.errors?.endDate &&
          isShortDateRangeSelected && (
            <BaseTypography variant="body2" color={brandColors.info} mt={2}>
              <BaseTypography variant="body2" component="span" fontWeight={600} marginRight={0.5}>
                Note:
              </BaseTypography>
              {SHORT_SCENARIO_WARNING_MESSAGE}
            </BaseTypography>
          )}
      </Stack>
    </FormCard>
  );
};

export default ScenarioInformationForm;
